/* ==========================================================================
   Little Karka – theme behaviour
   Vanilla custom elements; every feature degrades to plain HTML without JS.
   ========================================================================== */
(() => {
  'use strict';

  const LK = window.LK || { routes: {}, settings: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };

  /* ---------- Reveal + sew-in on scroll ---------- */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(entry.target.hasAttribute('data-sew') ? 'is-sewn' : 'is-visible');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );
  const observeReveals = (root = document) => {
    root.querySelectorAll('[data-reveal]:not(.is-visible), [data-sew]:not(.is-sewn)').forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Scroll thread with needle (easter egg) ---------- */
  const initScrollThread = () => {
    const bar = document.querySelector('.lk-thread-progress');
    if (!bar) return;
    let ticking = false;
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? clamp(window.scrollY / max, 0, 1) : 0;
      bar.style.setProperty('--lk-progress', p.toFixed(4));
      bar.style.setProperty('--lk-progress-visible', p > 0.01 && p < 0.995 ? '1' : '0');
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  };

  /* ---------- Header ---------- */
  class LKHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.drawer = this.querySelector('[data-lk-drawer]');

      window.addEventListener('scroll', () => this.classList.toggle('is-scrolled', window.scrollY > 8), { passive: true, signal });

      if (this.drawer) {
        this.drawer.addEventListener('toggle', () => {
          document.documentElement.style.overflow = this.drawer.open ? 'hidden' : '';
          if (this.drawer.open) this.drawer.querySelector('[data-lk-drawer-close]')?.focus();
        }, { signal });
        this.drawer.querySelector('[data-lk-drawer-close]')?.addEventListener('click', () => this.closeDrawer(), { signal });
        this.drawer.addEventListener('click', (e) => {
          // click on the dimmed backdrop (the details element itself, outside the panel)
          if (this.drawer.open && !e.target.closest('.lk-drawer__panel, summary')) this.closeDrawer();
        }, { signal });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.drawer?.open) this.closeDrawer();
        this.querySelectorAll('.lk-nav__dropdown[open]').forEach((d) => { d.open = false; d.querySelector('summary')?.focus(); });
      }, { signal });

      document.addEventListener('click', (e) => {
        this.querySelectorAll('.lk-nav__dropdown[open]').forEach((d) => { if (!d.contains(e.target)) d.open = false; });
      }, { signal });
    }
    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.open = false;
      this.drawer.querySelector('summary')?.focus();
    }
    disconnectedCallback() {
      this.abort?.abort();
      document.documentElement.style.overflow = '';
    }
  }
  define('lk-header', LKHeader);

  /* ---------- Announcement rotation ---------- */
  class LKAnnouncements extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.lk-announce__item')];
      if (this.items.length < 2 || reduceMotion.matches) return;
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 5000;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
    }
    start() { this.stop(); this.timer = setInterval(() => this.next(), this.interval); }
    stop() { clearInterval(this.timer); }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
    disconnectedCallback() { this.stop(); }
  }
  define('lk-announcements', LKAnnouncements);

  /* ---------- Product card: hoop loupe (easter egg) ---------- */
  class LKProductCard extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.lk-card__media[data-loupe-src]');
      if (!this.media || !LK.settings.easterEggs) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.media.addEventListener('pointerenter', () => this.prepare(), { signal });
      this.media.addEventListener('pointermove', (e) => this.move(e), { signal });
      this.media.addEventListener('pointerleave', () => this.hide(), { signal });
    }
    prepare() {
      if (this.loupe) return;
      this.loupe = document.createElement('span');
      this.loupe.className = 'lk-loupe';
      this.loupe.setAttribute('aria-hidden', 'true');
      this.loupe.style.backgroundImage = `url("${this.media.dataset.loupeSrc}")`;
      this.media.append(this.loupe);
    }
    move(e) {
      if (!this.loupe) this.prepare();
      const rect = this.media.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const zoom = 2.6;
      // image is object-fit: cover in a 4:5 box – approximate with the box size
      this.loupe.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
      const size = this.loupe.offsetWidth / 2;
      this.loupe.style.backgroundPosition = `${-(x * zoom - size)}px ${-(y * zoom - size)}px`;
      this.loupe.style.left = `${x}px`;
      this.loupe.style.top = `${y}px`;
      this.loupe.classList.add('is-active');
      this.media.classList.add('is-looping');
    }
    hide() {
      this.loupe?.classList.remove('is-active');
      this.media.classList.remove('is-looping');
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  define('lk-product-card', LKProductCard);

  /* ---------- Basket: AJAX add + toast ---------- */
  const toastEl = () => document.querySelector('[data-lk-toast]');
  let toastTimer;
  const showToast = (html) => {
    const el = toastEl();
    if (!el) return;
    el.innerHTML = html;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 5000);
  };
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const refreshCartCount = async () => {
    try {
      const res = await fetch(LK.routes.cartJs, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.hidden = cart.item_count === 0;
        const num = badge.querySelector('[data-cart-count-number]');
        if (num) num.textContent = cart.item_count;
        const sr = badge.querySelector('.lk-visually-hidden');
        if (sr) sr.textContent = sr.textContent.replace(/\d+/, cart.item_count);
        badge.classList.remove('is-bumped');
        void badge.offsetWidth;
        badge.classList.add('is-bumped');
      });
    } catch (_) { /* count refresh is best-effort */ }
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-lk-add]');
    if (!form || !LK.settings.cartToast) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const original = button?.innerHTML;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = LK.strings.adding; }
    try {
      const res = await fetch(LK.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || LK.strings.addError);
      await refreshCartCount();
      showToast(
        `<span class="lk-toast__text"><strong>${escapeHtml(data.product_title || '')}</strong><br>${escapeHtml(LK.strings.added)}</span>` +
        `<a href="${LK.routes.cart}">${escapeHtml(LK.strings.viewBasket)}</a>`
      );
      form.dispatchEvent(new CustomEvent('lk:added', { bubbles: true, detail: data }));
    } catch (err) {
      showToast(`<span class="lk-toast__text">${escapeHtml(err.message || LK.strings.addError)}</span>`);
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = original; }
    }
  });

  /* ---------- Hero hoop clamp (easter egg) ---------- */
  class LKHoop extends HTMLElement {
    connectedCallback() {
      this.clamp = this.querySelector('[data-hoop-clamp]');
      this.hoop = this.querySelector('.lk-hoop');
      this.message = this.querySelector('[data-hoop-message]');
      this.turns = 0;
      this.onClick = () => this.tighten();
      this.clamp?.addEventListener('click', this.onClick);
    }
    tighten() {
      this.turns += 1;
      this.clamp.querySelector('.lk-hoop__screw')?.style.setProperty('--lk-screw', `${this.turns * 180}deg`);
      this.hoop.classList.remove('is-tightened');
      void this.hoop.offsetWidth;
      this.hoop.classList.add('is-tightened');
      if (this.turns === 3 && this.message) {
        const notes = ['nice & taut!', 'perfect tension ✿', 'ready to stitch!'];
        this.message.textContent = notes[Math.floor(Math.random() * notes.length)];
        this.message.classList.add('is-shown');
        setTimeout(() => this.message.classList.remove('is-shown'), 2600);
        this.turns = 0;
      }
    }
    disconnectedCallback() { this.clamp?.removeEventListener('click', this.onClick); }
  }
  define('lk-hoop', LKHoop);

  /* ---------- Hero stitch story: machine embroidery -> finished tee ---------- */
  class LKStitchStory extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('.lk-stitch__svg');
      this.scene = this.querySelector('[data-scene]');
      this.motif = this.querySelector('[data-motif]');
      this.machine = this.querySelector('[data-machine]');
      this.spool = this.querySelector('[data-spool]');
      this.thread = this.querySelector('[data-thread]');
      this.toggle = this.querySelector('[data-story-toggle]');
      this.steps = this.closest('[data-story-root]')?.querySelector('[data-story-steps]');
      const data = this.querySelector('script[data-story-layers]');
      if (!this.svg || !data) return;

      // thread layers in stitch order (fills, then outline), each with its clip rect
      this.layers = JSON.parse(data.textContent).map((l) => ({
        ...l,
        clip: this.querySelector(`[data-clip="${l.name}"]`),
        // needle time is weighted by how much there is to sew
        weight: l.rows.reduce((sum, [, x0, x1]) => sum + (x1 - x0 + 1), 0),
      }));
      const strings = {};
      this.querySelector('template[data-story-strings]')?.content.querySelectorAll('[data-key]').forEach((s) => { strings[s.dataset.key] = s.textContent; });
      this.strings = strings;

      // timeline (ms): colours, outline, lift, zoom, photo hold
      this.T = { colours: 6200, outline: 2600, lift: 700, zoom: 1900, hold: 5200 };
      this.T.total = Object.values(this.T).reduce((a, b) => a + b, 0);
      const fills = this.layers.filter((l) => l.name !== 'outline');
      const fillWeight = fills.reduce((a, l) => a + l.weight, 0);
      let start = 0;
      for (const l of this.layers) {
        const dur = l.name === 'outline' ? this.T.outline : Math.max(450, (l.weight / fillWeight) * this.T.colours);
        l.start = start;
        l.end = start + dur;
        start = l.end;
      }
      this.sewEnd = start;
      this.T.total = this.sewEnd + this.T.lift + this.T.zoom + this.T.hold;

      // camera: close on the chest embroidery -> the tee as framed in the product photo
      this.cam0 = { k: 9.2, fx: 294.1, fy: 149.3, cx: 200, cy: 212 };
      this.cam1 = { k: 1, fx: 200, fy: 200, cx: 200, cy: 200 };

      if (reduceMotion.matches) { this.showFinal(); return; }

      this.classList.add('is-ready');
      this.paused = false;
      this.elapsed = 0;
      this.render(0);

      this.abort = new AbortController();
      this.toggle?.addEventListener('click', () => this.setPaused(!this.paused), { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal: this.abort.signal });
      this.observer = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; this.sync(); }, { threshold: 0.2 });
      this.observer.observe(this);
    }

    sync() {
      const shouldRun = this.inView && !this.paused && !document.hidden;
      if (shouldRun && !this.frame) {
        this.last = performance.now();
        this.frame = requestAnimationFrame((t) => this.tick(t));
      } else if (!shouldRun && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    tick(now) {
      this.elapsed = (this.elapsed + Math.min(now - this.last, 100)) % this.T.total;
      this.last = now;
      this.render(this.elapsed);
      this.frame = requestAnimationFrame((t) => this.tick(t));
    }

    setPaused(paused) {
      this.paused = paused;
      this.toggle.setAttribute('aria-pressed', String(paused));
      const label = this.toggle.querySelector('[data-story-toggle-label]');
      if (label) label.textContent = paused ? this.strings.play : this.strings.pause;
      this.classList.toggle('is-sewing', !paused && this.elapsed < this.sewEnd);
      this.sync();
    }

    // artwork point -> svg viewBox space (through motif + camera transforms)
    toSvg(x, y) {
      const m = this.svg.getScreenCTM().inverse().multiply(this.motif.getScreenCTM());
      const p = new DOMPoint(x, y).matrixTransform(m);
      return { x: p.x, y: p.y };
    }

    setCamera(t) {
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const { cam0: a, cam1: b } = this;
      const k = a.k * Math.pow(b.k / a.k, e); // geometric zoom feels natural
      const lerp = (p, q) => p + (q - p) * e;
      this.scene.setAttribute('transform', `translate(${lerp(a.cx, b.cx)} ${lerp(a.cy, b.cy)}) scale(${k}) translate(${-lerp(a.fx, b.fx)} ${-lerp(a.fy, b.fy)})`);
    }

    placeMachine(x, y, opacity = 1) {
      this.machine.setAttribute('transform', `translate(${x} ${y})`);
      this.machine.style.opacity = opacity;
    }

    // easter egg: the machine changes thread for every colour, like the real thing
    setThread(color) {
      if (this.threadColor === color) return;
      this.threadColor = color;
      this.spool.style.fill = color;
      this.thread.style.stroke = color;
    }

    setStep(name) {
      if (!this.steps || this.step === name) return;
      this.step = name;
      const order = ['colours', 'outline', 'wear'];
      const idx = order.indexOf(name);
      this.steps.querySelectorAll('[data-step]').forEach((li) => {
        const i = order.indexOf(li.dataset.step);
        li.classList.toggle('is-current', i === idx);
        li.classList.toggle('is-done', i < idx);
      });
    }

    setClip(layer, top) {
      layer.clip.setAttribute('y', top);
      layer.clip.setAttribute('height', Math.max(0, layer.y1 + 4 - top));
    }

    render(ms) {
      const { T } = this;
      const sewing = ms < this.sewEnd;
      if (!this.paused) this.classList.toggle('is-sewing', sewing);
      this.classList.toggle('is-final', ms >= this.sewEnd + T.lift + T.zoom && ms < T.total - 600);

      // 1–2. colour by colour, then the outline: each layer rises row by row
      if (sewing) {
        this.setCamera(0);
        for (const layer of this.layers) {
          if (ms >= layer.end) { this.setClip(layer, layer.y0 - 4); continue; }
          if (ms < layer.start) { this.setClip(layer, layer.y1 + 4); continue; }
          const f = (ms - layer.start) / (layer.end - layer.start);
          const top = layer.y1 + 4 - f * (layer.y1 - layer.y0 + 8);
          this.setClip(layer, top);
          // needle zig-zags across this colour's real width at the current row
          const row = layer.rows.reduce((best, r) => (Math.abs(r[0] - top) < Math.abs(best[0] - top) ? r : best), layer.rows[0]);
          const x = row[1] + (row[2] - row[1]) * (0.5 + 0.5 * Math.sin(ms / 30));
          const p = this.toSvg(x, Math.max(top, layer.y0));
          this.placeMachine(p.x, p.y);
          this.setThread(layer.color);
          this.setStep(layer.name === 'outline' ? 'outline' : 'colours');
        }
        return;
      }
      this.layers.forEach((l) => this.setClip(l, l.y0 - 4));
      let t = ms - this.sewEnd;

      // 3. machine lifts away
      if (t < T.lift) {
        const f = t / T.lift;
        const p = this.toSvg(400, 60);
        this.placeMachine(p.x, p.y - f * 240, 1 - f);
        this.setCamera(0);
        return;
      }
      t -= T.lift;
      this.placeMachine(200, -400, 0);

      // 4. zoom out: the fabric was the tee all along; 5. photo crossfade + hold
      this.setCamera(Math.min(1, t / T.zoom));
      this.setStep('wear');
    }

    showFinal() {
      this.classList.remove('is-ready');
      this.steps?.querySelectorAll('[data-step]').forEach((li) => li.classList.add('is-done'));
      if (this.toggle) this.toggle.hidden = true;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  }
  define('lk-stitch-story', LKStitchStory);

  /* ---------- Stitch vs print: wash test ---------- */
  class LKWashTest extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-wash-button]');
      this.label = this.querySelector('[data-wash-label]');
      this.count = this.querySelector('[data-wash-count]');
      this.status = this.querySelector('[data-wash-status]');
      this.total = Number(this.dataset.washes) || 50;
      const tpl = this.querySelector('template[data-wash-strings]');
      this.strings = {};
      tpl?.content.querySelectorAll('[data-key]').forEach((s) => { this.strings[s.dataset.key] = s.textContent; });
      this.done = false;
      this.onClick = () => (this.done ? this.reset() : this.run());
      this.button?.addEventListener('click', this.onClick);
    }
    run() {
      this.button.disabled = true;
      this.classList.add('is-washing');
      const duration = reduceMotion.matches ? 0 : 2600;
      const start = performance.now();
      const step = (now) => {
        const p = duration ? clamp((now - start) / duration, 0, 1) : 1;
        const eased = 1 - Math.pow(1 - p, 2);
        this.style.setProperty('--lk-wear', eased.toFixed(3));
        this.count.textContent = Math.round(eased * this.total);
        if (p < 1) { this.frame = requestAnimationFrame(step); return; }
        this.classList.remove('is-washing');
        this.button.disabled = false;
        this.done = true;
        this.label.textContent = this.strings.reset;
        this.status.textContent = this.strings.done;
      };
      this.frame = requestAnimationFrame(step);
    }
    reset() {
      this.style.setProperty('--lk-wear', '0');
      this.count.textContent = '0';
      this.status.textContent = '';
      this.label.textContent = this.strings.wash;
      this.done = false;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.button?.removeEventListener('click', this.onClick);
    }
  }
  define('lk-wash-test', LKWashTest);

  /* ---------- Craft process: thread sewn through the steps on scroll ---------- */
  class LKCraftPath extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('.lk-craft__svg');
      this.guide = this.querySelector('[data-craft-guide]');
      this.thread = this.querySelector('[data-craft-thread]');
      this.mask = this.querySelector('[data-craft-mask]');
      this.needle = this.querySelector('[data-craft-needle]');
      this.steps = [...this.querySelectorAll('[data-craft-step]')];
      if (!this.svg || this.steps.length < 2) return;
      this.abort = new AbortController();
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this);
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(() => { this.update(); ticking = false; }); }
      }, { passive: true, signal: this.abort.signal });
    }
    build() {
      const box = this.getBoundingClientRect();
      if (!box.width) return;
      const pts = this.steps.map((step) => {
        const r = step.querySelector('[data-craft-anchor]').getBoundingClientRect();
        return { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 };
      });
      let d = `M${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const flip = i % 2 ? -1 : 1;
        if (Math.abs(dx) > Math.abs(dy)) {
          const amp = flip * 70;
          d += ` C${a.x + dx * 0.4} ${a.y + amp} ${b.x - dx * 0.4} ${b.y + amp} ${b.x} ${b.y}`;
        } else {
          // stacked layout: a gentle wiggle that stays inside the hoop lane
          const amp = flip * 24;
          d += ` C${a.x + amp} ${a.y + dy * 0.4} ${b.x + amp} ${b.y - dy * 0.4} ${b.x} ${b.y}`;
        }
      }
      this.svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      [this.guide, this.thread, this.mask].forEach((p) => p.setAttribute('d', d));
      this.length = this.thread.getTotalLength();
      this.mask.style.strokeDasharray = `${this.length} ${this.length}`;
      // distance along the path where each step's hoop sits
      this.stops = pts.map((pt) => this.nearestLength(pt));
      this.svg.classList.add('is-ready');
      this.update();
    }
    nearestLength(pt) {
      let best = 0;
      let bestDist = Infinity;
      const samples = 160;
      for (let i = 0; i <= samples; i++) {
        const l = (this.length * i) / samples;
        const p = this.thread.getPointAtLength(l);
        const dist = (p.x - pt.x) ** 2 + (p.y - pt.y) ** 2;
        if (dist < bestDist) { bestDist = dist; best = l; }
      }
      return best;
    }
    update() {
      if (!this.length) return;
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      // sew over at least half a screen of scrolling, starting as the section enters
      const span = Math.max(rect.height, vh * 0.5);
      const p = reduceMotion.matches ? 1 : clamp((vh * 0.85 - rect.top) / span, 0, 1);
      const drawn = this.length * p;
      this.mask.style.strokeDashoffset = `${this.length - drawn}`;
      const pt = this.thread.getPointAtLength(drawn);
      const ahead = this.thread.getPointAtLength(Math.min(this.length, drawn + 2));
      const behind = this.thread.getPointAtLength(Math.max(0, drawn - 2));
      const angle = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI;
      this.needle.setAttribute('transform', `translate(${pt.x} ${pt.y}) rotate(${angle + 180})`);
      this.classList.toggle('is-active', p > 0 && p < 1);
      this.classList.toggle('is-complete', p >= 1);
      this.steps.forEach((step, i) => step.classList.toggle('is-stitched', drawn >= this.stops[i] - 4 && p > 0));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
    }
  }
  define('lk-craft-path', LKCraftPath);

  /* ---------- Cross-stitch sampler ---------- */
  class LKSampler extends HTMLElement {
    connectedCallback() {
      this.cells = [...this.querySelectorAll('[data-cell]')];
      this.cols = Number(this.dataset.cols);
      this.spools = [...this.querySelectorAll('[data-thread]')].filter((el) => el.classList.contains('lk-sampler__spool'));
      this.countEl = this.querySelector('[data-sampler-count]');
      this.reward = this.querySelector('[data-sampler-reward]');
      this.guide = this.cells.filter((c) => c.classList.contains('is-guide'));
      this.tool = 'terracotta';
      this.painting = false;
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.restore();

      this.spools.forEach((spool) => spool.addEventListener('click', () => this.selectTool(spool), { signal }));
      this.querySelector('.lk-sampler__threads')?.addEventListener('keydown', (e) => this.spoolKeys(e), { signal });

      const cloth = this.querySelector('.lk-sampler__cloth');
      cloth.addEventListener('pointerdown', (e) => {
        const cell = e.target.closest('[data-cell]');
        if (!cell) return;
        this.painting = e.pointerType !== 'touch';
        this.paint(cell);
        this.lastPainted = cell;
      }, { signal });
      cloth.addEventListener('pointerover', (e) => {
        if (!this.painting) return;
        const cell = e.target.closest('[data-cell]');
        if (cell && cell !== this.lastPainted) { this.paint(cell); this.lastPainted = cell; }
      }, { signal });
      window.addEventListener('pointerup', () => { this.painting = false; }, { signal });
      cloth.addEventListener('click', (e) => {
        // keyboard activation (Enter/Space) arrives as a click with detail 0
        const cell = e.target.closest('[data-cell]');
        if (cell && e.detail === 0) this.paint(cell);
      }, { signal });
      cloth.addEventListener('keydown', (e) => this.gridKeys(e), { signal });
      this.querySelector('[data-sampler-clear]')?.addEventListener('click', () => this.clear(), { signal });
    }
    selectTool(spool) {
      this.tool = spool.dataset.thread;
      this.spools.forEach((s) => s.setAttribute('aria-checked', String(s === spool)));
    }
    spoolKeys(e) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      e.preventDefault();
      const i = this.spools.findIndex((s) => s.getAttribute('aria-checked') === 'true');
      const next = this.spools[(i + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + this.spools.length) % this.spools.length];
      this.selectTool(next);
      next.focus();
    }
    gridKeys(e) {
      const cell = e.target.closest('[data-cell]');
      if (!cell) return;
      const i = this.cells.indexOf(cell);
      const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -this.cols, ArrowDown: this.cols, Home: -(i % this.cols), End: this.cols - 1 - (i % this.cols) };
      if (!(e.key in moves)) return;
      e.preventDefault();
      const next = this.cells[clamp(i + moves[e.key], 0, this.cells.length - 1)];
      cell.tabIndex = -1;
      next.tabIndex = 0;
      next.focus();
    }
    paint(cell) {
      if (this.tool === 'unpick') {
        delete cell.dataset.thread;
        cell.setAttribute('aria-pressed', 'false');
      } else {
        cell.dataset.thread = this.tool;
        cell.setAttribute('aria-pressed', 'true');
      }
      this.changed();
    }
    clear() {
      this.cells.forEach((c) => { delete c.dataset.thread; c.setAttribute('aria-pressed', 'false'); });
      this.reward.hidden = true;
      this.changed();
    }
    changed() {
      const stitched = this.cells.filter((c) => c.dataset.thread);
      this.countEl.textContent = stitched.length;
      if (this.guide.length && this.guide.every((c) => c.dataset.thread) && this.reward.hidden) {
        this.reward.hidden = false;
        document.dispatchEvent(new CustomEvent('lk:sampler-complete'));
      }
      this.save(stitched);
      document.dispatchEvent(new CustomEvent('lk:stitch', { detail: { count: stitched.length } }));
    }
    save(stitched) {
      try {
        localStorage.setItem(this.dataset.storageKey, JSON.stringify(stitched.map((c) => [c.dataset.cell, c.dataset.thread])));
      } catch (_) { /* storage unavailable */ }
    }
    restore() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '[]');
        const byKey = new Map(this.cells.map((c) => [c.dataset.cell, c]));
        saved.forEach(([key, thread]) => {
          const cell = byKey.get(key);
          if (cell) { cell.dataset.thread = thread; cell.setAttribute('aria-pressed', 'true'); }
        });
      } catch (_) { /* ignore */ }
      const stitched = this.cells.filter((c) => c.dataset.thread).length;
      this.countEl.textContent = stitched;
      if (this.guide.length && this.guide.every((c) => c.dataset.thread)) this.reward.hidden = false;
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  define('lk-sampler', LKSampler);

  /* ---------- Product page: variant picker ---------- */
  class LKProductForm extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[type="application/json"][data-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.idInput = this.querySelector('input[name="id"]');
      this.button = this.querySelector('[data-add-button]');
      this.buttonLabel = this.querySelector('[data-add-label]');
      this.priceTarget = document.querySelector(`[data-price-for="${this.dataset.section}"]`);
      this.onChange = () => this.update();
      this.addEventListener('change', this.onChange);
    }
    update() {
      const selected = [...this.querySelectorAll('fieldset[data-option]')].map((fs) => fs.querySelector('input:checked')?.value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      // reflect availability on each option value
      this.querySelectorAll('fieldset[data-option]').forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
          input.closest('.lk-pill')?.classList.toggle('is-unavailable', !match || !match.available);
        });
        const legendValue = fs.querySelector('[data-selected-value]');
        if (legendValue) legendValue.textContent = selected[index] || '';
      });
      if (!variant) {
        this.button.disabled = true;
        this.buttonLabel.textContent = this.dataset.unavailable;
        return;
      }
      this.idInput.value = variant.id;
      this.button.disabled = !variant.available;
      this.buttonLabel.textContent = variant.available ? this.dataset.add : this.dataset.soldOut;
      if (this.priceTarget) this.priceTarget.innerHTML = variant.price_html;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.media_id) {
        const media = document.querySelector(`[data-media-id="${variant.media_id}"]`);
        if (media) media.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
    }
    disconnectedCallback() { this.removeEventListener('change', this.onChange); }
  }
  define('lk-product-form', LKProductForm);

  /* ---------- Stitch counter in the footer (easter egg) ---------- */
  const initStitchCounter = () => {
    const el = document.querySelector('[data-stitch-counter]');
    if (!el) return;
    const text = el.querySelector('[data-stitch-counter-text]');
    const strings = {};
    el.querySelector('template')?.content.querySelectorAll('[data-key]').forEach((s) => { strings[s.dataset.key] = s.textContent; });
    let pageStitches = 0;
    let yours = 0;

    const tally = () => {
      let total = 0;
      document.querySelectorAll('.lk-seam__line').forEach((line) => {
        const seam = line.closest('.lk-seam');
        const per = seam.classList.contains('lk-seam--straight') ? 14 : seam.classList.contains('lk-seam--zigzag') ? 12 : 10.5;
        total += line.offsetWidth / per;
      });
      const dashed = '.lk-btn, .lk-card__media, .lk-patch__link, .lk-vs__card--stitch, .lk-story__frame, .lk-newsletter__card, .lk-craft__hoop, .lk-notes__label, .lk-badge, .lk-announce, .lk-header, .lk-ribbon__band, .lk-footer__hem';
      document.querySelectorAll(dashed).forEach((node) => { total += (2 * (node.offsetWidth + node.offsetHeight)) / 11; });
      document.querySelectorAll('[data-craft-thread]').forEach((p) => { try { total += p.getTotalLength() / 16; } catch (_) { /* not rendered */ } });
      return Math.round(total / 10) * 10;
    };
    const render = () => {
      const fmt = new Intl.NumberFormat(document.documentElement.lang || undefined);
      let msg = (strings.page || '').replace('__N__', fmt.format(pageStitches));
      if (yours > 0) msg += ' ' + (strings.you || '').replace('__M__', fmt.format(yours));
      text.textContent = msg;
      el.hidden = false;
    };
    const run = () => {
      pageStitches = tally();
      yours = document.querySelectorAll('.lk-sampler__cell[data-thread]').length; // restored sampler work
      render();
    };
    document.addEventListener('lk:stitch', (e) => { yours = e.detail.count; render(); });
    // wait for fonts/images so measured sizes are settled
    const later = () => setTimeout(run, 1200);
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  };

  /* ---------- Waitlist: heart designs, tag the sign-up ---------- */
  const initWaitlist = () => {
    const forms = document.querySelectorAll('form.lk-waitlist-form');
    const likes = document.querySelectorAll('[data-design-like]');
    const nudge = document.querySelector('[data-likes-nudge]');

    // after a sign-up Shopify lands on ?customer_posted=true, which keeps showing "you're in"
    // (and no form) on every reload – drop it so the next visit gets the form back
    const url = new URL(window.location.href);
    if (url.searchParams.has('customer_posted')) {
      url.searchParams.delete('customer_posted');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }

    const liked = () => [...likes].filter((b) => b.getAttribute('aria-pressed') === 'true');
    const listNames = (names, and) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} ${and} ${names.at(-1)}`);

    const sync = () => {
      const on = liked();
      forms.forEach((form) => {
        const input = form.querySelector('[data-waitlist-tags]');
        if (input) input.value = [input.dataset.base, ...on.map((b) => b.dataset.designLike)].filter(Boolean).join(',');
        const note = form.querySelector('[data-waitlist-likes]');
        if (!note) return;
        note.hidden = on.length === 0;
        note.textContent = (note.dataset.template || '').replace('__NAMES__', listNames(on.map((b) => b.dataset.designName), note.dataset.and || 'and'));
      });
      // only nudge when there's a form still waiting for an email
      if (nudge) nudge.hidden = on.length === 0 || !document.querySelector('form.lk-waitlist-form [name="contact[email]"]');
    };

    likes.forEach((button) => {
      button.addEventListener('click', () => {
        button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
        button.classList.remove('is-popped');
        void button.offsetWidth;
        button.classList.add('is-popped');
        setTimeout(() => button.classList.remove('is-popped'), 250);
        sync();
      });
    });

    // easter egg: try to peek under the cloth on the secret design
    document.querySelectorAll('[data-design-peek]').forEach((peek) => {
      const card = peek.closest('.lk-design');
      const message = card?.querySelector('[data-peek-message]');
      const hints = [...(card?.querySelector('template[data-peek-hints]')?.content.querySelectorAll('span') || [])].map((s) => s.textContent);
      let i = 0;
      let timer;
      peek.addEventListener('click', () => {
        peek.classList.add('is-peeking');
        clearTimeout(timer);
        timer = setTimeout(() => peek.classList.remove('is-peeking'), 900);
        if (message && hints.length) message.textContent = hints[i++ % hints.length];
      });
    });

    // store owner typed a wrong password: keep the form open to show the error
    document.querySelectorAll('details').forEach((d) => {
      if (!d.querySelector('.lk-form-message--error')) return;
      d.hidden = false;
      d.open = true;
    });

    // hidden owner login: /password#owner reveals it
    const ownerLogin = document.querySelector('[data-owner-login]');
    const revealOwner = () => {
      if (!ownerLogin || location.hash !== '#owner') return;
      ownerLogin.hidden = false;
      ownerLogin.open = true;
      ownerLogin.scrollIntoView({ block: 'center' });
      ownerLogin.querySelector('input[type="password"]')?.focus({ preventScroll: true });
    };
    revealOwner();
    window.addEventListener('hashchange', revealOwner);

    // policies start closed; an in-page link (e.g. the privacy note) opens its panel on click
    document.querySelectorAll('a[href^="#policy-"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const panel = document.getElementById(link.getAttribute('href').slice(1));
        if (!(panel instanceof HTMLDetailsElement)) return;
        e.preventDefault();
        panel.open = true;
        panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });
  };

  /* ---------- A note for the curious (console easter egg) ---------- */
  const consoleNote = () => {
    if (!LK.settings.easterEggs) return;
    const s1 = 'font: 600 16px Fredoka, sans-serif; color: #3A637F;';
    const s2 = 'font: 13px "Glacial Indifference", sans-serif; color: #48382B;';
    // eslint-disable-next-line no-console
    console.log(
      '%c\n  ✂ - - - - - - - - - - - - - - - - - - - - - -\n  You found the back of the fabric!\n%c  Like every Little Karka piece, this site was stitched with care.\n  No chemical prints were harmed in its making. 🧵\n',
      s1,
      s2
    );
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    observeReveals();
    initScrollThread();
    initStitchCounter();
    initWaitlist();
    consoleNote();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-observe after section reloads
  document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
})();
